import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileJourney } from '../types/fileAudit';
import { fileAuditApi } from '../services/api/fileAudit';
import { queryKeys } from '../lib/queryKeys';

/**
 * State returned by the useFileJourney hook
 */
export interface FileJourneyState {
  /** The file journey data, null if not loaded or fileId is not provided */
  journey: FileJourney | null;
  /** Whether the journey is currently being fetched */
  isLoading: boolean;
  /** Error message if the fetch failed, null otherwise */
  error: string | null;
}

/**
 * Return type for the useFileJourney hook
 */
export interface UseFileJourneyReturn extends FileJourneyState {
  /** Manually refresh the journey data */
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching and managing file journey data.
 *
 * THUB-299: was three useState values plus a `latestFileId` ref whose only job was discarding a
 * response for a file the user had already navigated away from. useQuery gives that for free --
 * each fileId is its own cache entry, so a late response for the previous file lands in the
 * previous file's entry and can never overwrite the current one.
 *
 * The key is deliberately NOT nested under `tasks`: `execution_updates` invalidates
 * queryKeys.tasks.all on every file event, which would refetch journeys nobody has open. The
 * modal's own refresh control remains the way to re-read, exactly as before.
 *
 * @param fileId - The ID of the file to fetch the journey for (can be null/undefined)
 * @returns The journey state and refresh function
 *
 * @example
 * const { journey, isLoading, error, refresh } = useFileJourney(selectedFileId);
 *
 * // Handle loading state
 * if (isLoading) return <LoadingSkeleton />;
 *
 * // Handle error state
 * if (error) return <ErrorMessage message={error} onRetry={refresh} />;
 *
 * // Render journey data
 * if (journey) return <FileJourneyTimeline stages={journey.stages} />;
 */
export function useFileJourney(fileId: string | null | undefined): UseFileJourneyReturn {
  const query = useQuery<FileJourney, unknown>({
    queryKey: queryKeys.fileAudit.journey(fileId ?? ''),
    queryFn: () => fileAuditApi.getFileJourney(fileId as string),
    enabled: !!fileId,
  });

  const { refetch } = query;

  const refresh = useCallback(async (): Promise<void> => {
    if (!fileId) return;
    await refetch();
  }, [fileId, refetch]);

  // Preserve the original message contract: an Error's own message, else a friendly fallback.
  const error = query.error
    ? query.error instanceof Error
      ? query.error.message
      : 'Failed to fetch file journey. Please try again.'
    : null;

  return {
    // `enabled: false` leaves data undefined; the contract is null.
    journey: query.data ?? null,
    isLoading: query.isFetching,
    error,
    refresh,
  };
}

export default useFileJourney;
